import type { B2BResponsesSide, B2BViewerRole } from './types';

export const formatHourlyRate = (pricePerHour: string | null): string => {
  if (!pricePerHour) return '';
  const parsed = Number(pricePerHour);
  if (!Number.isFinite(parsed)) return '—';
  return `$${Math.round(parsed)}/hr`;
};

export const formatCurrency = (value: string | null): string => {
  if (!value) return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(2)}M`;
  if (num >= 1_000) {
    const rounded = Math.round(num / 1_000);
    if (rounded >= 1000) return `$${(num / 1_000_000).toFixed(2)}M`;
    return `$${rounded}K`;
  }
  return `$${num.toLocaleString()}`;
};

export const formatRelativeTime = (dateStr: string | null): string => {
  if (!dateStr) return '';
  const then = new Date(dateStr).getTime();
  if (isNaN(then)) return '';
  const now = Date.now();
  const diffMs = now - then;
  if (diffMs < 0) return '';
  const minutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);
  const weeks = Math.floor(days / 7);

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days === 1) return '1 day ago';
  if (days < 7) return `${days} days ago`;
  if (weeks === 1) return '1 week ago';
  return `${weeks} weeks ago`;
};

export const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '—';
  const parsed = Date.parse(dateStr);
  if (isNaN(parsed)) return '—';
  return new Date(parsed).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

export const formatShortDate = (dateStr: string | null): string => {
  if (!dateStr) return '—';
  const parsed = Date.parse(dateStr);
  if (isNaN(parsed)) return '—';
  return new Date(parsed).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

export const formatWorkLocation = (location: string): string => {
  const map: Record<string, string> = {
    ONSITE: 'Onsite',
    REMOTE: 'Remote',
    PARTIAL_REMOTE: 'Hybrid',
  };
  return map[location] ?? location;
};

// [MCV-1889] A response closed because another response on its listing became the contract. It
// reads as a neutral "closed", never as the red "Declined", and offers no actions.
export const LISTING_FILLED_LABEL = 'Closed: listing filled';
export const LISTING_FILLED_NOTE = 'Another response on this listing became a contract.';

export const isListingFilled = (row: { statusReason?: string | null }): boolean =>
  row.statusReason === 'LISTING_FILLED';

/** The B2B Responses tab a response lives under: the buyer's Buying tab, the supplier's Selling tab. */
export const b2bSideForViewerRole = (
  viewerRole: B2BViewerRole | null | undefined
): B2BResponsesSide | undefined =>
  viewerRole === 'BUYER' ? 'buying' : viewerRole === 'SUPPLIER' ? 'selling' : undefined;
